import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import { logger } from '@core/utils';
import { authReducer, authApi, initializeAuth, setCredentials, logout } from '../../features/auth';
import { chatReducer, chatApi } from '../../features/chat';
import { filesApi } from '../../features/files';
import { foldersApi } from '../../features/files/api/foldersApi';
import { auditLogsApi } from '../../features/files/api/auditLogsApi';
import { faqApi } from '../../features/faq/api/faqApi';
import { usersApi } from '../../features/users/api/usersApi';
import { emergencyApi, substitutesApi, emergencyResponseApi } from '../../features/emergency-substitution/api';
import { basesApi } from '../../features/knowledge-bases';
import { emergencyFiltersReducer, emergencySelectionReducer } from '../../features/emergency-substitution/store';
import { locationAvailabilityApi } from '../../features/public-responses';
import { workflowsApi, instancesApi, employeesApi } from '../../features/onboarding/api';
import { engagementPulseApi } from '../../features/engagement-pulse/api';
import { conversationsApi } from '../../features/admin-conversations';
import { publicStepApi } from '../../features/onboarding-public';
import { brandingApi } from '../../features/auth/api/brandingApi';

/**
 * Listener middleware for handling side effects
 * Keeps reducers pure by moving localStorage operations here
 */
const listenerMiddleware = createListenerMiddleware();

// Listen for setCredentials action and persist to localStorage
listenerMiddleware.startListening({
  actionCreator: setCredentials,
  effect: (action) => {
    const { user, token } = action.payload;
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('userData', JSON.stringify(user));
    } catch (error) {
      logger.error('Error saving auth to localStorage:', error);
    }
  },
});

// Listen for logout action and clear localStorage
listenerMiddleware.startListening({
  actionCreator: logout,
  effect: () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
    } catch (error) {
      logger.error('Error clearing auth from localStorage:', error);
    }
  },
});

/**
 * Redux store configuration using Redux Toolkit
 *
 * Features registered:
 * - auth: Authentication state and user data
 * - authApi: RTK Query API for authentication endpoints
 * - chat: Chat messages and conversations
 * - chatApi: RTK Query API for chat endpoints
 * - filesApi: RTK Query API for file management endpoints
 * - foldersApi: RTK Query API for folder management endpoints
 * - auditLogsApi: RTK Query API for file audit logs endpoints
 * - faqApi: RTK Query API for FAQ management endpoints
 * - usersApi: RTK Query API for user management endpoints
 * - emergencyApi: RTK Query API for emergency substitution operations (consolidated)
 * - substitutesApi: RTK Query API for substitute candidate management
 * - emergencyResponseApi: RTK Query API for public emergency response endpoints
 * - locationAvailabilityApi: RTK Query API for public location availability campaigns
 * - emergencyFilters: Emergency substitution filter state
 * - emergencySelection: Emergency substitution candidate selection state
 * - workflowsApi: RTK Query API for onboarding workflow management
 * - instancesApi: RTK Query API for onboarding instance management
 * - employeesApi: RTK Query API for employee management
 * - conversationsApi: RTK Query API for admin conversation monitoring
 */
export const store = configureStore({
  reducer: {
    auth: authReducer,
    chat: chatReducer,
    emergencyFilters: emergencyFiltersReducer,
    emergencySelection: emergencySelectionReducer,
    [authApi.reducerPath]: authApi.reducer,
    [chatApi.reducerPath]: chatApi.reducer,
    [filesApi.reducerPath]: filesApi.reducer,
    [foldersApi.reducerPath]: foldersApi.reducer,
    [auditLogsApi.reducerPath]: auditLogsApi.reducer,
    [faqApi.reducerPath]: faqApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [emergencyApi.reducerPath]: emergencyApi.reducer,
    [substitutesApi.reducerPath]: substitutesApi.reducer,
    [emergencyResponseApi.reducerPath]: emergencyResponseApi.reducer,
    [locationAvailabilityApi.reducerPath]: locationAvailabilityApi.reducer,
    [basesApi.reducerPath]: basesApi.reducer,
    [workflowsApi.reducerPath]: workflowsApi.reducer,
    [instancesApi.reducerPath]: instancesApi.reducer,
    [employeesApi.reducerPath]: employeesApi.reducer,
    [engagementPulseApi.reducerPath]: engagementPulseApi.reducer,
    [conversationsApi.reducerPath]: conversationsApi.reducer,
    [publicStepApi.reducerPath]: publicStepApi.reducer,
    [brandingApi.reducerPath]: brandingApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types for serialization checks
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
        // Ignore plain object in emergencyApi getCandidateResponses as it's intentionally used for O(1) lookup
        ignoredPaths: ['emergencyApi.queries'],
      },
    })
      .concat(listenerMiddleware.middleware)
      .concat(authApi.middleware)
      .concat(chatApi.middleware)
      .concat(filesApi.middleware)
      .concat(foldersApi.middleware)
      .concat(auditLogsApi.middleware)
      .concat(faqApi.middleware)
      .concat(usersApi.middleware)
      .concat(emergencyApi.middleware)
      .concat(substitutesApi.middleware)
      .concat(emergencyResponseApi.middleware)
      .concat(locationAvailabilityApi.middleware)
      .concat(basesApi.middleware)
      .concat(workflowsApi.middleware)
      .concat(instancesApi.middleware)
      .concat(employeesApi.middleware)
      .concat(engagementPulseApi.middleware)
      .concat(conversationsApi.middleware)
      .concat(publicStepApi.middleware)
      .concat(brandingApi.middleware),
  devTools: process.env.NODE_ENV !== 'production',
});

/**
 * Initialize authentication from localStorage on app start
 *
 * Redux Toolkit 2025 Best Practice:
 * - Store is created with empty auth state
 * - Immediately dispatch initializeAuth to load from localStorage
 * - This pattern makes the initialization fully testeable
 */
store.dispatch(initializeAuth());
